// One advance and where it went: opened from Bank (an advance row's number).
// Shows the advance's figures and every invoice it was applied to.

import { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useSettings } from '../hooks/useSettings';
import { fmtMoney } from '../utils/currency';
import { shortInvoiceNo } from '../utils/invoiceNo';
import { Button, Modal, ModalBanner } from './ui';
import type { AdvanceApplication, CustomerAdvance, SupplierAdvance } from '../types';

function fmtDay(d?: string) {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function AdvanceDetailModal({ kind, advanceId, entityName, onOpenInvoice, onClose }: {
  kind: 'customer' | 'supplier';
  advanceId: string;
  entityName: (id?: string) => string;
  /** Opens an applied invoice (e.g. the sale popup); left out, the number is plain text. */
  onOpenInvoice?: (invoiceId: string) => void;
  onClose: () => void;
}) {
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);
  const coll = kind === 'customer' ? 'customerAdvances' : 'supplierAdvances';
  const [adv, setAdv] = useState<CustomerAdvance | SupplierAdvance | null>(null);
  const [apps, setApps] = useState<AdvanceApplication[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'missing'>('loading');

  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    let live = true;
    Promise.all([getDoc(doc(db, coll, advanceId)), getDocs(collection(db, coll, advanceId, 'applications'))])
      .then(([a, ap]) => {
        if (!live) return;
        if (!a.exists()) { setState('missing'); return; }
        setAdv({ id: a.id, ...a.data() } as CustomerAdvance | SupplierAdvance);
        setApps(ap.docs.map(d => ({ id: d.id, ...d.data() } as AdvanceApplication))
          .sort((x, y) => (y.date ?? '').localeCompare(x.date ?? '')));
        setState('ready');
      })
      .catch(() => { if (live) setState('missing'); });
    return () => { live = false; };
  }, [coll, advanceId]);

  const partyId = adv ? (kind === 'customer' ? (adv as CustomerAdvance).toEntityId : (adv as SupplierAdvance).fromEntityId) : undefined;
  const applied = adv?.appliedUSD ?? 0;
  const left = Math.max(0, (adv?.amountUSD ?? 0) - applied);
  const appTotal = apps.reduce((s, a) => s + (a.amountUSD ?? 0), 0);
  const pagePath = kind === 'customer' ? '/sales/advances' : '/purchase/advances';

  return (
    <Modal open onClose={onClose} size="lg" hideHead
      footer={
        <div style={{ display: 'flex', gap: 8, width: '100%', alignItems: 'center' }}>
          <a href={pagePath} target="_blank" rel="noopener noreferrer" className="line-item-link-btn" style={{ marginRight: 'auto' }}>
            Open {kind === 'customer' ? 'Customer' : 'Supplier'} Advances
          </a>
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      }
    >
      <ModalBanner
        title={partyId ? entityName(partyId) : (kind === 'customer' ? 'Customer Advance' : 'Supplier Advance')}
        eyebrow={kind === 'customer' ? 'Customer Advance' : 'Supplier Advance'}
        right={adv && (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 2 }}>Advance No</div>
            <span className="sm-ref" title={adv.advanceNo}>{shortInvoiceNo(adv.advanceNo)}</span>
          </div>
        )}
        onClose={onClose}
      />

      {state === 'loading' ? (
        <div className="skeleton" style={{ height: 160, borderRadius: 8 }} />
      ) : state === 'missing' || !adv ? (
        <div style={{ padding: '24px 0', textAlign: 'center', fontSize: 13, color: 'var(--text-3)' }}>This advance could not be found. It may have been deleted.</div>
      ) : (<>
        <div className="adv-sum">
          <div><span>{kind === 'customer' ? 'Received' : 'Paid'}</span><strong>{fmtDay(adv.date)}</strong></div>
          <div><span>Advance</span><strong>{money(adv.amountUSD)}</strong></div>
          <div><span>Applied</span><strong style={{ color: 'var(--primary)' }}>{money(applied)}</strong></div>
          <div><span>Left</span><strong style={{ color: left > 0.005 ? 'var(--text-1)' : 'var(--success)' }}>{money(left)}</strong></div>
          <div><span>Account</span><strong style={{ fontFamily: 'inherit', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis' }} title={adv.bankAccountName}>{adv.bankAccountName || '—'}</strong></div>
        </div>

        <div className="form-section-label" style={{ margin: '16px 0 8px' }}>Applied To</div>
        {apps.length === 0 ? (
          <div style={{ padding: '20px 16px', textAlign: 'center', fontSize: 13, color: 'var(--text-3)', border: '1px dashed var(--border)', borderRadius: 'var(--r-sm)' }}>
            Not applied to any invoice yet.
          </div>
        ) : (
          <div style={{ border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', overflow: 'auto', maxHeight: 320 }}>
            <table className="adv-apply-table">
              <thead>
                <tr>
                  <th style={{ width: 36, textAlign: 'right' }}>#</th>
                  <th>Date</th>
                  <th>Invoice No</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {apps.map((ap, i) => (
                  <tr key={ap.id}>
                    <td style={{ textAlign: 'right', color: 'var(--text-3)', fontFamily: 'var(--font-num)' }}>{i + 1}</td>
                    <td style={{ fontFamily: 'var(--font-num)' }}>{fmtDay(ap.date)}</td>
                    <td>
                      {onOpenInvoice ? (
                        <button type="button" className="sm-link" title={`Open ${ap.invoiceNo}`} onClick={() => onOpenInvoice(ap.invoiceId)}>
                          <span className="sm-ref">{shortInvoiceNo(ap.invoiceNo)}</span>
                        </button>
                      ) : <span className="sm-ref" title={ap.invoiceNo}>{shortInvoiceNo(ap.invoiceNo)}</span>}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', color: 'var(--text-1)', fontWeight: 600 }}>{money(ap.amountUSD)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}>Total · {apps.length} invoice{apps.length !== 1 ? 's' : ''}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)' }}>{money(appTotal)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </>)}
    </Modal>
  );
}
